/** Error body shapes the API may return: `{error, code}`, ProblemDetails, or plain text. */
export interface ApiErrorBody {
  error?: string;
  code?: string;
  title?: string;
  detail?: string;
  retryAfter?: number;
  [key: string]: unknown;
}

/** A non-2xx JSON response. */
export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly body?: ApiErrorBody;
  /** Seconds to wait before retrying, from the body's `retryAfter` or the `Retry-After` header. */
  readonly retryAfter?: number;

  constructor(
    message: string,
    status: number,
    opts: { code?: string; body?: ApiErrorBody; retryAfter?: number } = {}
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = opts.code;
    this.body = opts.body;
    this.retryAfter = opts.retryAfter;
  }

  get isUnauthorized(): boolean {
    return this.status === 401 || this.code === 'auth_expired';
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }
}

export function isAbortError(e: unknown): boolean {
  return e instanceof Error && e.name === 'AbortError';
}

/** Turn a failed response into an `ApiError` (ProblemDetails, `{error}` and plain text are all accepted). */
export async function errorFromResponse(response: Response): Promise<ApiError> {
  let body: ApiErrorBody | undefined;
  let text = '';
  try {
    text = await response.text();
    body = text ? (JSON.parse(text) as ApiErrorBody) : undefined;
  } catch {
    body = undefined;
  }
  const message =
    body?.error ||
    body?.detail ||
    body?.title ||
    (text && text.length < 300 && !text.startsWith('<') ? text : '') ||
    `HTTP ${response.status}`;
  return new ApiError(message, response.status, {
    code: body?.code,
    body,
    retryAfter: retryAfterSeconds(response, body),
  });
}

/** `retryAfter` in the body wins; the `Retry-After` header is the HTTP fallback (seconds or an HTTP date). */
function retryAfterSeconds(response: Response, body: ApiErrorBody | undefined): number | undefined {
  if (typeof body?.retryAfter === 'number' && isFinite(body.retryAfter)) return Math.max(0, body.retryAfter);
  const header = response.headers?.get?.('Retry-After');
  if (!header) return undefined;
  const seconds = Number(header);
  if (isFinite(seconds)) return Math.max(0, seconds);
  const at = Date.parse(header);
  return isNaN(at) ? undefined : Math.max(0, Math.round((at - Date.now()) / 1000));
}
